import React, { useCallback, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';

import {
  ActivityLogNoteDetails,
  ActivityLogNote_ActivityLogFragment,
  useActivityLogDeleteNoteMutation,
  useActivityLogEditNoteMutation,
} from 'src/generated/mloop-graphql';

import DeleteNoteModal from 'src/components/ActivityLogs/HelperModals/Notes/DeleteNoteModal';
import EditNoteModal from 'src/components/ActivityLogs/HelperModals/Notes/EditNoteModal';

import ActivityLogBase from 'src/entities/ActivityLog/Helpers/ActivityLogBase';
import ActivityLogCommentBody from 'src/entities/ActivityLog/Helpers/ActivityLogCommentBody';
import ActivityLogContent from 'src/entities/ActivityLog/Helpers/ActivityLogContent';
import { MenuOption } from 'src/entities/ActivityLog/types';

import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useEmployeeId from 'src/hooks/useEmployeeId';

interface Fragment {
  activityLog: ActivityLogNote_ActivityLogFragment;
}

type ActivityLogContentShape = {
  markdown?: string | null;
  plaintext?: string | null;
};

enum ActivityLogNoteMenuOptionIds {
  EditNote = 'edit-note',
  DeleteNote = 'delete-note',
}

// Menu options are created dynamically in the component to support i18n

// eslint-disable-next-line modernloop/restric-fragments-name.cjs
const ActivityLogNote: FCWithFragments<Fragment> = ({ activityLog }) => {
  const { t } = useTranslation('hiring-dashboard-common');
  const refetchData = useActivityFeedRefetch();
  const { enqueueSnackbar } = useSnackbar();

  const thisEmployeeId = useEmployeeId();

  const menuOptions: MenuOption[] = useMemo(
    () => [
      {
        id: ActivityLogNoteMenuOptionIds.EditNote,
        value: t('common.activityLog.editComment'),
      },
      {
        id: ActivityLogNoteMenuOptionIds.DeleteNote,
        value: t('common.activityLog.deleteComment'),
      },
    ],
    [t]
  );

  const { details, activityActorEmployeeId } = activityLog;
  const noteData = details as ActivityLogNoteDetails;

  const doesEmployeeHaveEditAccess = useMemo(
    () => thisEmployeeId === activityActorEmployeeId,
    [activityActorEmployeeId, thisEmployeeId]
  );

  const [activityLogEditNote] = useActivityLogEditNoteMutation();
  const [activityLogDeleteNote] = useActivityLogDeleteNoteMutation();

  const [noteValue, setNoteValue] = useState(noteData?.note ?? '');
  const noteContent = (noteData as ActivityLogNoteDetails & { content?: ActivityLogContentShape | null })?.content;
  const noteMarkdown = noteContent?.markdown;
  const notePlaintext = noteContent?.plaintext;

  const [isEditNoteModalOpen, setIsEditNoteModalOpen] = useState(false);
  const [isDeleteNoteModalOpen, setIsDeleteNoteModalOpen] = useState(false);

  const onMenuOptionSelect = useCallback((option: MenuOption) => {
    switch (option.id) {
      case ActivityLogNoteMenuOptionIds.EditNote:
        setIsEditNoteModalOpen(true);
        break;
      case ActivityLogNoteMenuOptionIds.DeleteNote:
        setIsDeleteNoteModalOpen(true);
        break;
      default:
        break;
    }
  }, []);

  const onEditNoteModalClose = useCallback(() => {
    setIsEditNoteModalOpen(false);
  }, []);

  const onDeleteNoteModalClose = useCallback(() => {
    setIsDeleteNoteModalOpen(false);
  }, []);

  const onEditNoteModalSubmit = useCallback(
    (newNote: string) => {
      setIsEditNoteModalOpen(false);

      if (isEmpty(newNote)) {
        setIsDeleteNoteModalOpen(true);
      } else {
        activityLogEditNote({
          variables: {
            input: {
              activityLogId: activityLog.id,
              note: newNote.trim(),
            },
          },
        }) // eslint-disable-next-line promise/always-return
          .then(() => {
            setNoteValue(newNote);
          })
          .catch(() => {
            enqueueSnackbar(t('common.activityLog.noteUpdateFailed'), { variant: 'error' });
          });
      }
    },
    [activityLog.id, activityLogEditNote, enqueueSnackbar, t]
  );

  const onDeleteNoteModalSubmit = useCallback(() => {
    // eslint-disable-next-line promise/catch-or-return
    activityLogDeleteNote({ variables: { input: { activityLogId: activityLog.id } } }) // eslint-disable-next-line promise/always-return
      .then(() => {
        refetchData();
      })
      .catch(() => {
        enqueueSnackbar(t('common.activityLog.commentDeleteFailed'), { variant: 'error' });
      })
      .finally(() => {
        setIsDeleteNoteModalOpen(false);
      });
  }, [activityLog.id, activityLogDeleteNote, enqueueSnackbar, refetchData, t]);

  return (
    <>
      <ActivityLogBase
        activityLog={activityLog}
        actionLabelKey="hiring-dashboard-candidates:candidates.activityLog.addedComment"
        moreMenuOptions={doesEmployeeHaveEditAccess ? menuOptions : undefined}
        moreMenuDataTestId="activity-log--note-more-menu"
        onMoreMenuOptionSelect={onMenuOptionSelect}
      >
        <ActivityLogContent
          sx={{
            borderRadius: '6px 12px 12px 12px',
          }}
          content={
            <Stack direction="column" justifyContent="space-between" alignItems="flex-start">
              <ActivityLogCommentBody
                legacyText={notePlaintext?.trim() ? notePlaintext : noteValue}
                markdown={noteMarkdown}
              />
              {noteData?.isEdited ? (
                <Typography variant="body2" color="text.secondary">
                  {t('hiring-dashboard-tasks:tasks.rescheduleLog.edited', { defaultValue: 'Edited' })}
                </Typography>
              ) : null}
            </Stack>
          }
        />
      </ActivityLogBase>
      {isEditNoteModalOpen && noteValue ? (
        <EditNoteModal
          onSubmit={onEditNoteModalSubmit}
          note={noteValue}
          onClose={onEditNoteModalClose}
          open={isEditNoteModalOpen}
        />
      ) : null}

      <DeleteNoteModal
        onSubmit={onDeleteNoteModalSubmit}
        onClose={onDeleteNoteModalClose}
        open={isDeleteNoteModalOpen}
      />
    </>
  );
};

ActivityLogNote.fragments = {
  activityLog: gql`
    ${ActivityLogBase.fragments.activityLog}
    fragment ActivityLogNote_activityLog on ActivityLog {
      id
      activityActorEmployeeId
      details {
        ... on ActivityLogNoteDetails {
          __typename
          note
          isEdited
          content {
            markdown
            plaintext
            formatVersion
            editorStateJson
          }
        }
      }
      ...ActivityLogBase_activityLog
    }
  `,
};

export default ActivityLogNote;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ActivityLogEditNoteMutation = gql`
  ${ActivityLogNote.fragments.activityLog}
  mutation ActivityLogEditNote($input: ActivityNoteEditInput!, $showTaskName: Boolean! = false) {
    activityNoteEdit(input: $input) {
      activityLog {
        ...ActivityLogNote_activityLog
      }
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ActivityLogDeleteNoteMutation = gql`
  mutation ActivityLogDeleteNote($input: ActivityNoteDeleteInput!) {
    activityNoteDelete(input: $input) {
      result
    }
  }
`;
